import { baseCleanProps } from "common/api/Api";
import { FolderRecord, IFolderData } from "common/records/Folder";
import { fetcher } from "common/api/Fetcher";
import { AssociationType } from "common/types";
import { FolderParamsRecord } from "common/records/index/FolderParams";
import { toParams } from "common/records/index/QueryParamsBase";

export const url = "api/v1/folders";

export interface IFolderIndexResponse {
  folders: IFolderData[];
  parents: IFolderData[];
}

export interface IFolderOptions {
  folderable_id: number;
  folderable_type: AssociationType;
  name: string;
}

export interface IFolderResponse {
  folder: IFolderData;
}

export const list = (queryParams: FolderParamsRecord): Promise<IFolderIndexResponse> => {
  const parameters = toParams(queryParams);

  return fetcher.get<IFolderIndexResponse>(url, parameters, { useGlobalNotFoundRedirect: false });
};

/**
 * Resolves a folderable's root folder by name (e.g. the Org's "Estimate Templates" folder) and lists
 * its children. Unlike `list`, this does not require the caller to already know the folder id — the
 * server looks it up from `{ folderable_type, folderable_id, name }`. The API responds 204 (no body)
 * when the named folder doesn't exist; `fetcher` surfaces that as an empty response.
 */
export const byName = (options: IFolderOptions): Promise<IFolderIndexResponse> => {
  const actionUrl = fetcher.joinUrls(url, "by_name");

  return fetcher.get<IFolderIndexResponse>(actionUrl, options);
};

export const updateOrCreate = (folder: FolderRecord): Promise<IFolderResponse> => {
  if (folder.id > 0) {
    return update(folder);
  } else {
    return create(folder);
  }
};

export const create = (folder: FolderRecord): Promise<IFolderResponse> => {
  return fetcher.post<IFolderResponse>(url, { folder: cleanProps(folder), ...folder.createParams.toJS() });
};

export const destroy = (id: number): Promise<IFolderResponse> => {
  const actionUrl = fetcher.joinUrls(url, id.toString());
  return fetcher.delete(actionUrl);
};

export const update = (folder: FolderRecord): Promise<IFolderResponse> => {
  const actionUrl = fetcher.joinUrls(url, folder.id.toString());
  return fetcher.patch(actionUrl, cleanProps(folder));
};

export const cleanProps = (folder: FolderRecord): IFolderData => {
  const folderParams = baseCleanProps(folder.toJS() as any);

  if (folderParams.parent_id <= 0) {
    delete folderParams.parent_id;
  }

  delete folderParams.createParams;

  return folderParams;
};
